import React, { useState, useEffect, useCallback } from 'react';
import { getDailyProgressReports } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  Clock,
  Calendar,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CloudRain,
  Shield,
  Briefcase,
  Camera,
  CheckCircle,
  RefreshCw,
  FileText,
  User,
  Hash,
  MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';

// TruncatedText component defined outside the main component
const TruncatedText = ({ text, isExpanded, onToggle, maxLength = 200 }) => {
  const shouldTruncate = text && text.length > maxLength;
  
  if (!text) return null;
  
  return (
    <div className="relative">
      <p className={`text-sm text-white whitespace-pre-wrap break-words overflow-hidden ${
        !isExpanded && shouldTruncate ? 'max-h-[100px]' : ''
      }`}>
        {isExpanded || !shouldTruncate ? text : `${text.substring(0, maxLength)}...`}
      </p>
      {shouldTruncate && (
        <Button
          variant="link"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="text-blue-400 p-0 h-auto text-xs mt-1"
        >
          {isExpanded ? 'View Less' : 'View More...'}
        </Button>
      )}
    </div>
  );
};

// StatusBadge component defined outside
const StatusBadge = ({ status }) => {
  if (status === 'on_time') {
    return (
      <Badge className="bg-green-600 text-white text-xs">
        <CheckCircle className="w-3 h-3 mr-1" />
        On Time
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-500 text-white text-xs">
      <Clock className="w-3 h-3 mr-1" />
      Late
    </Badge>
  );
};

const DailyProgressHistory = ({ workOrderId, woNumber, onRefresh }) => {
  const [reports, setReports] = useState([]);
  const [reportsByDate, setReportsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedReports, setExpandedReports] = useState({});
  const [expandedText, setExpandedText] = useState({});
  const [todayCount, setTodayCount] = useState(0);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDailyProgressReports(workOrderId, 100);
      setReports(response.data.reports || []);
      setReportsByDate(response.data.reports_by_date || {});
      setTodayCount(response.data.today_count || 0);
      onRefresh?.();
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load progress history');
    } finally {
      setLoading(false);
    }
  }, [workOrderId, onRefresh]);

  useEffect(() => {
    if (workOrderId) {
      fetchReports();
    }
  }, [workOrderId, fetchReports]);

  const toggleExpand = (reportId) => {
    setExpandedReports(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  const toggleTextExpand = (reportId, field) => {
    const key = `${reportId}_${field}`;
    setExpandedText(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    
    if (date.toDateString() === today) return 'Today';
    if (date.toDateString() === yesterday) return 'Yesterday';
    
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (isoStr) => {
    return new Date(isoStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper to check if text is expanded
  const isTextExpanded = (reportId, field) => {
    const key = `${reportId}_${field}`;
    return expandedText[key] || false;
  };

  // Helper to toggle text expansion
  const handleTextToggle = (reportId, field) => {
    toggleTextExpand(reportId, field);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 mx-auto mb-3 text-slate-500" />
        <p className="text-slate-400">No daily progress reports yet</p>
        <p className="text-sm text-slate-500 mt-1">
          Reports will appear here after submission
        </p>
      </div>
    );
  }

  // Group reports by date
  const groupedReports = reports.reduce((acc, report) => {
    const date = report.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(report);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-white">Progress History</h3>
          <Badge variant="outline" className="border-slate-600 text-slate-300">
            {reports.length} updates
          </Badge>
          {todayCount > 0 && (
            <Badge className="bg-blue-600 text-white">
              {todayCount} today
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={fetchReports} className="border-slate-600 text-slate-300 hover:bg-slate-700">
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Reports grouped by date */}
      <ScrollArea className="h-[500px] pr-2">
        <div className="space-y-4">
          {Object.entries(groupedReports)
            .sort(([a], [b]) => new Date(b) - new Date(a))
            .map(([date, dateReports]) => (
            <div key={date} className="space-y-2">
              {/* Date Header */}
              <div className="flex items-center gap-2 sticky top-0 bg-slate-800 py-2 z-10">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-semibold text-white">{formatDate(date)}</span>
                {dateReports.length > 1 && (
                  <Badge className="bg-blue-600 text-white text-xs">
                    <Hash className="w-3 h-3 mr-1" />
                    {dateReports.length} updates
                  </Badge>
                )}
              </div>

              {/* Reports for this date */}
              {dateReports.map((report) => {
                const isExpanded = expandedReports[report.id];
                
                return (
                  <Card key={report.id} className="bg-slate-700 border-slate-600 overflow-hidden">
                    <CardContent className="p-0">
                      {/* Summary Row */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-slate-600 transition-colors"
                        onClick={() => toggleExpand(report.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            {/* Time label and percent */}
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <span className="font-bold text-green-400 text-xl">
                                {report.percent_complete}%
                              </span>
                              <StatusBadge status={report.status} />
                              {report.time_label && (
                                <Badge variant="outline" className="border-slate-500 text-slate-300 text-xs">
                                  {report.time_label}
                                </Badge>
                              )}
                              {report.safety_flag && (
                                <Badge className="bg-red-600 text-white text-xs">
                                  <Shield className="w-3 h-3 mr-1" />
                                  Safety
                                </Badge>
                              )}
                              {report.union_flag && (
                                <Badge className="bg-amber-600 text-white text-xs">
                                  <Briefcase className="w-3 h-3 mr-1" />
                                  Union
                                </Badge>
                              )}
                            </div>
                            
                            {/* Time and hours */}
                            <div className="flex items-center gap-4 text-xs text-slate-300">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTime(report.submitted_at)}
                              </span>
                              <span>{report.hours_worked}h worked</span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {report.created_by_name}
                              </span>
                            </div>

                            {/* Preview of work completed (truncated) */}
                            {!isExpanded && report.work_completed_text && (
                              <p className="text-sm text-slate-400 mt-2 line-clamp-2 break-words">
                                {report.work_completed_text}
                              </p>
                            )}
                          </div>
                          
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white shrink-0">
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t border-slate-500 bg-slate-600 p-4 space-y-4">
                          {/* Work Completed */}
                          <div>
                            <h5 className="text-xs font-semibold text-slate-300 uppercase mb-2">
                              Work Completed
                            </h5>
                            <div className="p-3 bg-slate-800 rounded-lg border border-slate-500 max-h-[200px] overflow-y-auto">
                              <TruncatedText 
                                text={report.work_completed_text} 
                                isExpanded={isTextExpanded(report.id, 'work')}
                                onToggle={() => handleTextToggle(report.id, 'work')}
                                maxLength={500}
                              />
                            </div>
                          </div>

                          {/* Delays/Issues */}
                          {report.delays_issues_text && (
                            <div>
                              <h5 className="text-xs font-semibold text-amber-300 uppercase mb-2 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Delays / Issues
                              </h5>
                              <div className="p-3 bg-amber-900/40 rounded-lg border border-amber-600 max-h-[150px] overflow-y-auto">
                                <TruncatedText 
                                  text={report.delays_issues_text} 
                                  isExpanded={isTextExpanded(report.id, 'delays')}
                                  onToggle={() => handleTextToggle(report.id, 'delays')}
                                />
                              </div>
                            </div>
                          )}

                          {/* Material Needed */}
                          {report.material_needed_text && (
                            <div>
                              <h5 className="text-xs font-semibold text-slate-300 uppercase mb-2">
                                Material/Equipment Needed
                              </h5>
                              <div className="p-3 bg-slate-800 rounded-lg border border-slate-500 max-h-[150px] overflow-y-auto">
                                <TruncatedText 
                                  text={report.material_needed_text} 
                                  isExpanded={isTextExpanded(report.id, 'material')}
                                  onToggle={() => handleTextToggle(report.id, 'material')}
                                />
                              </div>
                            </div>
                          )}

                          {/* Weather Impact */}
                          {report.weather_impact && (
                            <div className="p-3 bg-blue-900/40 rounded-lg border border-blue-600">
                              <h5 className="text-xs font-semibold text-blue-300 uppercase mb-1 flex items-center gap-1">
                                <CloudRain className="w-3 h-3" />
                                Weather Impact
                              </h5>
                              <p className="text-sm text-white break-words">
                                {report.weather_impact_notes || 'Weather affected work'}
                              </p>
                            </div>
                          )}

                          {/* Safety Details */}
                          {report.safety_flag && (
                            <div className="p-3 bg-red-900/40 rounded-lg border border-red-600">
                              <h5 className="text-xs font-semibold text-red-300 uppercase mb-1 flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                Safety Concern
                              </h5>
                              <p className="text-sm text-white break-words">
                                {report.safety_details || 'Safety concern flagged'}
                              </p>
                            </div>
                          )}

                          {/* Union Details */}
                          {report.union_flag && (
                            <div className="p-3 bg-amber-900/40 rounded-lg border border-amber-600">
                              <h5 className="text-xs font-semibold text-amber-300 uppercase mb-1 flex items-center gap-1">
                                <Briefcase className="w-3 h-3" />
                                Union/Overtime Concern
                              </h5>
                              <p className="text-sm text-white break-words">
                                {report.union_details || 'Union/overtime concern flagged'}
                              </p>
                            </div>
                          )}

                          {/* AI Review */}
                          {report.safety_review && (
                            <div className="p-3 bg-purple-900/40 rounded-lg border border-purple-600">
                              <h5 className="text-xs font-semibold text-purple-300 uppercase mb-1 flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                AI Safety/Union Review
                              </h5>
                              <p className="text-xs text-purple-200 mb-2">
                                Reviewed: {new Date(report.safety_review.reviewed_at).toLocaleString()}
                              </p>
                              <ScrollArea className="max-h-[150px]">
                                <p className="text-sm text-purple-200 whitespace-pre-wrap break-words">
                                  {report.safety_review.review_text}
                                </p>
                              </ScrollArea>
                            </div>
                          )}

                          {/* Photos */}
                          {report.photo_ids && report.photo_ids.length > 0 && (
                            <div className="flex items-center gap-2 text-slate-400">
                              <Camera className="w-4 h-4" />
                              <span className="text-sm">{report.photo_ids.length} photo(s) attached</span>
                            </div>
                          )}

                          {/* Footer info */}
                          <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-700">
                            <span>Project Status: <span className="text-slate-300 capitalize">{report.project_status}</span></span>
                            {report.update_sequence > 1 && (
                              <span className="text-blue-400">Update #{report.update_sequence} for this day</span>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default DailyProgressHistory;
